/** Event-level tab list (inside the Event Setup section). Placement of tickets on tabs is per ticket. */
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { ArrowDownIcon, ArrowUpIcon, PlusIcon, XCircleIcon } from "@heroicons/react/24/solid";
import { createApolloClient } from "../../../../providers/ApolloClientFactory";
import { userStore } from "../../../../store/user";
import { PrimaryButton, SecondaryButton } from "../../../../components/Button";
import ConfirmDialog from "../../../../components/ConfirmDialog";
import { DELETE_TICKETING_TAB, UPSERT_TICKETING_TABS } from "../../ticket-items/mutations";
import { DEFAULT_TAB_NAME } from "../../ticket-items/tabLayout";
import { SetupData } from "../types";
import { Chip } from "../ui";

const client = createApolloClient();
const { getState: getUserState } = userStore;

type DraftTab = { key: string; id?: number; name: string; is_active: boolean };

const ArrowButton = ({ dir, disabled, onClick, label }: { dir: "up" | "down"; disabled: boolean; onClick: () => void; label: string }) => (
  <button type="button" onClick={onClick} disabled={disabled} aria-label={label}
    className="h-7 w-7 inline-flex items-center justify-center rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed">
    {dir === "up" ? <ArrowUpIcon className="h-3.5 w-3.5" /> : <ArrowDownIcon className="h-3.5 w-3.5" />}
  </button>
);

export const TabsManager = ({ data, refresh }: { data: SetupData; refresh: () => void }) => {
  const event = data.event!;
  const [tabs, setTabs] = useState<DraftTab[]>([]);
  const [removed, setRemoved] = useState<number[]>([]);
  const [confirmKey, setConfirmKey] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setTabs([...data.tabs].sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name)).map((x) => ({ key: `tab-${x.id}`, id: x.id, name: x.name, is_active: x.is_active })));
    setRemoved([]);
  }, [data.tabs]);

  const countFor = (tab: DraftTab) => (tab.id ? data.tickets.filter((t) => t.tab_id === tab.id).length : 0);
  const move = (i: number, d: -1 | 1) => setTabs((p) => { const to = i + d; if (to < 0 || to >= p.length) return p; const n = [...p]; const [m] = n.splice(i, 1); n.splice(to, 0, m); return n; });
  const remove = (key: string) => {
    const tab = tabs.find((t) => t.key === key);
    if (tab?.id) setRemoved((p) => [...p, tab.id as number]);
    setTabs((p) => p.filter((t) => t.key !== key));
    setConfirmKey(null);
  };
  const dirty = removed.length > 0 || tabs.some((t) => !t.id) || tabs.some((t, i) => { const s = data.tabs.find((x) => x.id === t.id); return s && (s.name !== t.name || s.is_active !== t.is_active || s.sort_order !== i); });

  const save = async () => {
    const names = tabs.map((t) => t.name.trim());
    if (names.some((n) => !n)) return toast.error("Every tab needs a name.");
    if (new Set(names.map((n) => n.toLowerCase())).size !== names.length) return toast.error("Two tabs have the same name.");
    setSaving(true);
    try {
      for (const id of removed) await client.mutate({ mutation: DELETE_TICKETING_TAB, variables: { id } });
      if (tabs.length) {
        await client.mutate({
          mutation: UPSERT_TICKETING_TABS,
          variables: { objects: tabs.map((t, i) => ({ ...(t.id ? { id: t.id } : {}), event_id: event.id, name: t.name.trim(), sort_order: i, is_active: t.is_active, last_updated_by: getUserState().user?.id })) },
        });
      }
      await client.clearStore();
      toast.success("Tabs saved");
      refresh();
    } catch (err) {
      toast.error("Failed to save tabs: " + err);
    } finally {
      setSaving(false);
    }
  };

  const confirmTab = confirmKey ? tabs.find((t) => t.key === confirmKey) : null;

  return (
    <div className="border border-gray-200 rounded-md p-4 flex flex-col gap-3">
      <div>
        <div className="text-sm font-semibold text-gray-900">Tabs on the public page</div>
        <div className="text-sm text-gray-600">In this order. Which ticket goes on which tab is set per ticket, in its Placement step. Tickets on no tab show on a default "{DEFAULT_TAB_NAME}" tab at the end.</div>
      </div>
      {tabs.length === 0 ? (
        <div className="text-sm text-gray-500">No tabs. The site shows one plain list.</div>
      ) : (
        <div className="flex flex-col gap-2">
          {tabs.map((tab, i) => (
            <div key={tab.key} className="flex items-center gap-2">
              <span className="w-6 text-right text-xs text-gray-400">{i + 1}</span>
              <input type="text" value={tab.name} placeholder="Tab name (e.g. Admission, Shuttles)" maxLength={60}
                onChange={(e) => setTabs((p) => p.map((t) => (t.key === tab.key ? { ...t, name: e.target.value } : t)))}
                className="flex-1 min-w-0 max-w-md rounded border border-gray-300 px-2 py-1 text-sm focus:border-red-500 focus:ring-red-500" />
              <Chip>{countFor(tab)} ticket{countFor(tab) === 1 ? "" : "s"}</Chip>
              <label className="flex items-center gap-1 text-xs text-gray-600 whitespace-nowrap">
                <input type="checkbox" checked={tab.is_active} onChange={(e) => setTabs((p) => p.map((t) => (t.key === tab.key ? { ...t, is_active: e.target.checked } : t)))} /> Shown
              </label>
              <ArrowButton dir="up" disabled={i === 0} onClick={() => move(i, -1)} label="Move tab up" />
              <ArrowButton dir="down" disabled={i === tabs.length - 1} onClick={() => move(i, 1)} label="Move tab down" />
              <button type="button" className="text-gray-400 hover:text-red-600" aria-label="Remove tab" onClick={() => (countFor(tab) ? setConfirmKey(tab.key) : remove(tab.key))}>
                <XCircleIcon className="h-5 w-5" />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between">
        <SecondaryButton onClick={() => setTabs((p) => [...p, { key: `new-${Date.now()}`, name: "", is_active: true }])}><PlusIcon className="h-4 w-4 mr-1" /> Add tab</SecondaryButton>
        <PrimaryButton isDisabled={saving || !dirty} onClick={save}>{saving ? "Saving…" : "Save tabs"}</PrimaryButton>
      </div>
      <ConfirmDialog
        open={!!confirmTab}
        title="Remove tab?"
        message={<p>"{confirmTab?.name || "This tab"}" has {confirmTab ? countFor(confirmTab) : 0} ticket(s). They move to the default "{DEFAULT_TAB_NAME}" tab. The removal is applied when you save.</p>}
        confirmLabel="Remove"
        onConfirm={() => confirmKey && remove(confirmKey)}
        onCancel={() => setConfirmKey(null)}
      />
    </div>
  );
};
